/**
 * AdminSecurityPanel — visible to super-admin AND managers.
 *
 * Two areas:
 *  1) Security overview (score + hardening checklist + advice)
 *  2) Real-time event feed (login failures, brute-force, admin actions…)
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, ShieldAlert, Loader2, CheckCircle2, XCircle, Bell, RefreshCw, AlertTriangle,
} from 'lucide-react';
import { useAdminLang } from '../hooks/useAdminLang';

const severityStyle = (s) => ({
  info:     'bg-blue-500/15 text-blue-300 border-blue-400/30',
  warn:     'bg-amber-500/15 text-amber-300 border-amber-400/30',
  critical: 'bg-red-500/15 text-red-300 border-red-400/30',
}[s] || 'bg-white/10 text-white/70 border-white/15');

const AdminSecurityPanel = () => {
  const { at } = useAdminLang();
  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, e] = await Promise.all([
        axios.get('/api/admin/security/overview'),
        axios.get('/api/admin/security/events?only_active=false'),
      ]);
      setOverview(o.data);
      setEvents(e.data?.events || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const ack = async (id) => {
    try {
      await axios.post(`/api/admin/security/events/${id}/ack`);
      setEvents(prev => prev.map(e => e.id === id ? { ...e, acknowledged: true } : e));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (loading && !overview) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const score = overview?.score ?? 0;
  const scoreColor = score >= 90 ? 'text-emerald-300' : score >= 70 ? 'text-amber-300' : 'text-red-300';

  return (
    <div className="space-y-4" data-testid="admin-security-panel">
      <div className="rounded-2xl bg-gradient-to-br from-indigo-500/10 to-emerald-500/10 border border-indigo-400/30 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-indigo-300 flex-shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <div className="font-bold text-white mb-1">{at('securityTitle')}</div>
            <p className="text-white/70">{at('securitySubtitle')}</p>
          </div>
          <button onClick={load} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60" data-testid="security-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Score */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-white/80">{at('securityScore')}</span>
          <span className={`text-3xl font-bold ${scoreColor}`} data-testid="security-score">{score}<span className="text-lg opacity-70">/100</span></span>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-4">
          <div
            className={`h-full ${score >= 90 ? 'bg-emerald-400' : score >= 70 ? 'bg-amber-400' : 'bg-red-400'} transition-all duration-500`}
            style={{ width: `${Math.max(2, score)}%` }}
          />
        </div>
        <ul className="space-y-1.5" data-testid="security-checks">
          {(overview?.checks || []).map(c => (
            <li key={c.id} className="flex items-center gap-2 text-sm">
              {c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
              <span className={c.ok ? 'text-white/80' : 'text-white/50'}>{c.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Alerts */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-300" />
            <span className="text-sm font-semibold text-white/80">{at('securityAlerts')}</span>
          </div>
          <span className="text-xs text-white/50">{events.filter(e => !e.acknowledged).length} actives</span>
        </div>
        {events.length === 0 ? (
          <div className="text-center py-6 text-white/40 border border-dashed border-white/10 rounded-xl">
            {at('securityNoAlerts')}
          </div>
        ) : (
          <ul className="space-y-2 max-h-96 overflow-y-auto" data-testid="security-events">
            {events.map(e => (
              <li key={e.id} className={`rounded-xl border p-3 ${severityStyle(e.severity)} ${e.acknowledged ? 'opacity-50' : ''}`} data-testid={`sec-event-${e.id}`}>
                <div className="flex items-start gap-2">
                  {e.severity === 'critical' ? <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold uppercase">{e.severity}</span>
                      <span className="text-xs font-mono">{e.kind}</span>
                    </div>
                    <div className="text-xs opacity-80 mt-0.5 truncate">{JSON.stringify(e.details || {})}</div>
                    <div className="text-[10px] opacity-60 mt-0.5">{e.ip} · {new Date(e.created_at).toLocaleString()}</div>
                  </div>
                  {!e.acknowledged && (
                    <button onClick={() => ack(e.id)} className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/15" data-testid={`sec-ack-${e.id}`}>OK</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Advice */}
      {overview?.advice?.length > 0 && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <div className="text-sm font-semibold text-white/80 mb-2">{at('securityAdvice')}</div>
          <ul className="space-y-1 text-xs text-white/70">
            {overview.advice.map((a, i) => (
              <li key={i} className="flex items-start gap-2"><span className="text-fuchsia-300">›</span> {a}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AdminSecurityPanel;
